import { FormattedMessage } from 'react-intl';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import TableChartIcon from '@mui/icons-material/TableChart';
import { formatTagLabel } from 'lib/helpers';
import TagSuggestionsAutocomplete from 'components/mui/Layouts/components/TagSuggestionsAutocomplete';
import CreateFavoriteTagsModal from 'pages/company/WorkspaceRoute/components/FavoriteTagsPanel/components/CreateFavoriteTagsModal';

import useWorkspaceSearch from 'pages/company/WorkspaceRoute/components/WorkspaceSearch/useWorkspaceSearch';
import { useGridFilterChips } from 'pages/company/WorkspaceRoute/components/WorkspaceSearch/useGridFilterChips';
import { splitOverflow, type QueryChip } from 'pages/company/WorkspaceRoute/components/WorkspaceSearch/chips';
import { MAX_VISIBLE_CHIPS } from 'pages/company/WorkspaceRoute/components/WorkspaceSearch/constants';
import WorkspaceSearchInput from 'pages/company/WorkspaceRoute/components/WorkspaceSearch/WorkspaceSearchInput';
import type { RenderChip } from 'pages/company/WorkspaceRoute/components/WorkspaceSearch/types';

const DATE_TYPES: QueryChip['type'][] = ['date', 'invoice_date', 'payment_date'];
const TAG_PREFIX = '#';

// Consolidated Workspace search: free-text + #-suggestions + active filter chips
// (query-derived + grid-filter, unified for overflow) + Save-to-favorite + Clear-all,
// in one rectangular input. Re-parents presentation only; state stays where it lives.
const WorkspaceSearch = () => {
  const {
    inputValue,
    onInputChange,
    onSelectSuggestion,
    displayThresholdWarning,
    queryChips,
    removeQueryChip,
    gridFilters,
    removeGridFilter,
    clearAll,
    hasActiveFilters,
    canSaveFavorite,
    checkUserTag,
    favorite,
  } = useWorkspaceSearch();

  const gridChips = useGridFilterChips(gridFilters);

  const queryRenderChips: RenderChip[] = queryChips.map((chip) => {
    const user = checkUserTag(chip.value);
    return {
      id: `q:${chip.type}:${chip.value}`,
      title: chip.value,
      avatar: user ? <Avatar src={user.picture} slotProps={{ img: { loading: 'lazy' } }} /> : undefined,
      label: (
        <>
          {DATE_TYPES.includes(chip.type) && (
            <>
              <FormattedMessage id={`documents.tags.${chip.type}`} defaultMessage="Date" />
              &nbsp;
            </>
          )}
          {user ? user.username : formatTagLabel(chip.value)}
        </>
      ),
      onDelete: () => removeQueryChip(chip),
    };
  });

  const gridRenderChips: RenderChip[] = gridChips.map((g) => ({
    id: `g:${g.key}`,
    title: `${g.title}: ${g.text}`,
    label: `${g.title}: ${g.text}`,
    // Origin marker: the same icon the Workspace view toggle uses for grid, so a
    // grid-column filter reads as distinct from a query-tag chip (task 16.1).
    icon: <TableChartIcon />,
    onDelete: () => removeGridFilter(g.key),
  }));

  const { visible, hidden } = splitOverflow([...queryRenderChips, ...gridRenderChips], MAX_VISIBLE_CHIPS);

  return (
    <>
      <Box sx={{ flexGrow: 1, maxWidth: 800 }}>
        <TagSuggestionsAutocomplete
          inputValue={inputValue}
          onInputChange={onInputChange}
          onSelect={onSelectSuggestion}
          requirePrefix={TAG_PREFIX}
          prefixOutputWithHashtag
          renderInput={(params) => (
            <WorkspaceSearchInput
              params={params}
              visibleChips={visible}
              hiddenChips={hidden}
              thresholdWarning={displayThresholdWarning}
              hasActiveFilters={hasActiveFilters}
              canSaveFavorite={canSaveFavorite}
              onSaveFavorite={favorite.handleToggleModal}
              onClearAll={clearAll}
            />
          )}
        />
      </Box>
      {favorite.isOpenModal && (
        <CreateFavoriteTagsModal
          onClose={favorite.handleToggleModal}
          onSubmit={favorite.handleSaveTags}
          title={favorite.modalTitle}
          resetError={favorite.resetErrorMeta}
          errorMeta={favorite.errorMeta}
        />
      )}
    </>
  );
};

export default WorkspaceSearch;
